import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetCollectionTicketClaims } from '@/api/trafikportalen';
import { FormElement } from '@/components/FormElement';
import { FreePeriodBadge } from '@/components/Organizations/FreePeriodBadge';
import { PeriodStatusBadge } from '@/components/Organizations/PeriodStatusBadge';
import { PeriodTicketKind } from '@/components/Organizations/PeriodTicketKind';
import { PeriodDateRange } from '@/components/PeriodDateRange';
import { Spinner } from '@/components/core/Spinner';
import { getPeriodStatus } from '@/helpers/periodStatus';
import { useOrganizationStaticTicketTitles } from '@/hooks/useStaticTicketTitles';
import type { CollectionTypeDTO } from '@/api/trafikportalen/models/collectionTypeDTO';

interface TicketPeriodProps {
  claimId: string;
  collectionId?: string;
  collectionType?: CollectionTypeDTO;
}

export const TicketPeriod = ({
  claimId,
  collectionId,
  collectionType,
}: TicketPeriodProps) => {
  const { formatMessage } = useIntl();
  const ticketTitle = useOrganizationStaticTicketTitles(collectionType);
  const {
    data: claims,
    isLoading,
    isError,
  } = useGetCollectionTicketClaims(collectionId ?? '', {
    query: { enabled: !!collectionId },
  });

  const period = useMemo(
    () => claims?.find((claim) => claim.id === claimId),
    [claims, claimId],
  );

  if (!collectionId || isError || (!isLoading && !period)) {
    return (
      <FormElement
        label={formatMessage({ defaultMessage: 'Tilldelande period:' })}
        input={
          <p className="italic text-text-700">
            <FormattedMessage defaultMessage="Perioden kunde inte hämtas" />
          </p>
        }
      />
    );
  }

  if (isLoading || !period) {
    return (
      <FormElement
        label={formatMessage({ defaultMessage: 'Tilldelande period:' })}
        input={<Spinner className="size-5" />}
      />
    );
  }

  return (
    <FormElement
      label={formatMessage({ defaultMessage: 'Tilldelande period:' })}
      input={
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <PeriodDateRange from={period.valid_from} to={period.valid_to} />
            <PeriodStatusBadge status={getPeriodStatus(period)} />
            {period.non_invoiced && <FreePeriodBadge />}
          </div>
          <span className="text-text-700">
            <PeriodTicketKind
              relational={period.relational}
              staticTicketName={ticketTitle(period.stina_product_id)}
            />
          </span>
        </div>
      }
    />
  );
};
